// Brand kit fonts in the editor. The editor otherwise only loads Google Fonts
// (<link> tags); a brand font is a file in our storage, so it is registered
// with the FontFace API under its family name. Text boxes then just use that
// family in `textFontFamily`.
//
// Editor only: the Visor and exports still accept Google Fonts hosts only
// (core/visor/main.es6, font_extractor.es6), so there a brand font falls back
// to the next family in the stack.

import { getBrandKit } from "../services/brandKitService";
import { credentialsFor } from "../../common/utils";

const loaded = new Map(); // asset id → Promise<boolean>
let orgFonts = null; // the one library load of this page

/** Test hook. */
export const __resetBrandFonts = () => {
  loaded.clear();
  orgFonts = null;
};

/** The family a brand font registers as — its metadata family, else the asset name. */
export const brandFontFamily = (asset) =>
  (asset && asset.metadata && asset.metadata.family) || (asset && asset.name) || "Brand font";

/** CSS `font-family` value for text set in this brand font. */
export const brandFontCss = (asset) => `'${brandFontFamily(asset).replace(/'/g, "")}', sans-serif`;

/** Register one brand font with the document. Resolves false (never throws) if it can't. */
export function loadBrandFont(asset) {
  if (!asset || !asset.url) return Promise.resolve(false);
  if (loaded.has(asset.id)) return loaded.get(asset.id);
  if (typeof FontFace === "undefined" || typeof document === "undefined" || !document.fonts) {
    return Promise.resolve(false);
  }
  const meta = asset.metadata || {};
  if (meta.source === "google") return remember(asset, loadGoogleFont(asset));
  // Fetched as bytes rather than `url(...)`: a FontFace url() load is a CORS
  // request that never carries cookies, so the gated /api/f/<id> route (8080
  // -> 4000, cookie-mode asset gate) answers 401.
  const promise = fetch(asset.url, credentialsFor(asset.url))
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.arrayBuffer();
    })
    .then((bytes) =>
      new FontFace(brandFontFamily(asset), bytes, {
        weight: String(meta.weight || 400),
        style: meta.style || "normal",
      }).load()
    )
    .then((face) => {
      document.fonts.add(face);
      return true;
    })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.warn("[brandFonts] could not load", asset.url, e && e.message);
      loaded.delete(asset.id); // let a later click try again
      return false;
    });
  return remember(asset, promise);
}

function remember(asset, promise) {
  loaded.set(asset.id, promise);
  return promise;
}

// A font picked from the Google Fonts list: its stylesheet is public, so a
// plain <link> loads it (no cookie gate, no FontFace). Ready once the browser
// can resolve the family. The editor's and Visor's own Google font loaders
// pick the family up from textFontFamily too, so it also renders there.
function loadGoogleFont(asset) {
  const family = brandFontFamily(asset);
  const exists = Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')).some(
    (l) => l.getAttribute("href") === asset.url
  );
  if (!exists) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = asset.url;
    document.head.appendChild(link);
  }
  return Promise.resolve(document.fonts.load(`16px "${family.replace(/"/g, "")}"`))
    .then((faces) => !!(faces && faces.length))
    .catch(() => false)
    .then((ok) => {
      if (!ok) loaded.delete(asset.id);
      return ok;
    });
}

/**
 * Register every font of the organization's brand library — run at editor
 * start so text set in a brand font renders right after a reload. Resolves to
 * how many fonts loaded; any failure just means fewer (never throws). The
 * library is asked once per page: the editor calls this on every toolbar
 * change, and a font picked later loads through loadBrandFont itself.
 */
export function loadOrgBrandFonts() {
  if (!orgFonts) orgFonts = fetchOrgBrandFonts();
  return orgFonts;
}

async function fetchOrgBrandFonts() {
  try {
    const kit = await getBrandKit();
    const fonts = (kit && kit.assets && kit.assets.font) || [];
    const results = await Promise.all(fonts.map(loadBrandFont));
    return results.filter(Boolean).length;
  } catch {
    return 0;
  }
}
